import React, { useState, useEffect } from 'react';
import Modal from '@shared/ui/Modal';
import { Vote, CheckCircle, XCircle, Minus, UserX, StickyNote, Save, Loader2, ChevronRight } from 'lucide-react';
import { api } from '../App';
import Chip from './Chip';
import { formatLegislatorNameFromObject } from '../utils/nameUtils';
import { formatPartyDisplay, matchesPartyFilter } from '../utils/partyUtils';
import { EditIcon } from './icons';
import { CURRENT_SESSION } from '../hooks/useSessions';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from './StandardTable';

interface VotePredictionModalProps {
  isOpen: boolean;
  onClose: () => void;
  billId: string;
  billNumber: string;
  billTitle: string;
}

interface Legislator {
  id: number;
  full_name: string;
  first_name?: string;
  last_name?: string;
  party_affiliation: string;
  chamber: string;
  district: number;
  committees: string;
  predicted_vote?: string;
  confidence_level?: string;
  prediction_notes?: string;
  assignment_method?: string;
  prediction_updated_at?: string;
}

interface Workspace {
  id: string;
  name: string;
  access_level: string;
}

const VotePredictionModal: React.FC<VotePredictionModalProps> = ({
  isOpen,
  onClose,
  billId,
  billNumber,
  billTitle
}) => {
  const [step, setStep] = useState<'predictions' | 'workspaces'>('predictions');
  const [legislators, setLegislators] = useState<Legislator[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedLegislators, setExpandedLegislators] = useState<Set<number>>(new Set());
  const [editingNotes, setEditingNotes] = useState<Set<number>>(new Set());
  const [tempNotes, setTempNotes] = useState<{[key: number]: string}>({});
  
  // Filter state
  const [filters, setFilters] = useState({
    chamber: 'All',
    committee: 'All committees',
    party: 'All parties'
  });
  const [filterOptions, setFilterOptions] = useState<{
    chambers: string[];
    parties: string[];
    committees: Array<{ name: string; chamber: string; displayName: string }>;
  }>({
    chambers: [],
    parties: [],
    committees: []
  });

  // Reset modal when opened
  useEffect(() => {
    if (isOpen) {
      setStep('predictions');
      setExpandedLegislators(new Set());
      setEditingNotes(new Set());
      setTempNotes({});
      setSelectedWorkspaces([]);
      fetchLegislators();
      fetchFilterOptions();
      fetchWorkspaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, billId]);

  // Fetch legislators for this bill
  const fetchLegislators = async () => {
    if (!billId) return;
    
    setLoading(true);
    try {
      // First, get the bill details to determine the session
      const billResponse = await api.get(`/bills/${billId}`);
      
      if (!billResponse.data.success) {
        throw new Error('Failed to fetch bill details');
      }
      
      const bill = billResponse.data.data;
      /*
        Roster for the bill's own session. Prefer the bill's session_key over
        slicing its label, and fall back to the 'current' sentinel rather than a
        wall-clock year — a January fallback would ask for a session that has no
        roster yet and return an empty prediction grid. The server resolves the
        sentinel against the registry.
      */
      const sessionYear =
        bill.session_key || (bill.session ? bill.session.substring(0, 4) : CURRENT_SESSION);

      // Fetch all legislators for the session (without pagination to get all)
      const response = await api.get(`/legislators?session_year=${sessionYear}`);
      
      if (response.data.success && response.data.data && response.data.data.legislators) {
        // Transform the data to match our expected format - note: data is in data.legislators
        const legislatorsData = response.data.data.legislators.map((legislator: any) => ({
          id: legislator.id,
          /*
            GET /legislators serialises camelCase — fullName / firstName /
            lastName / partyAffiliation. This mapper read full_name and
            party_affiliation, so both came back undefined and every row rendered
            a blank name and an empty party chip. `chamber` was the one key whose
            spelling happened to match, which is why chamber was the only column
            with anything in it.

            Both spellings are accepted so the mapper survives whichever shape the
            endpoint returns.
          */
          full_name: legislator.fullName ?? legislator.full_name,
          first_name: legislator.firstName ?? legislator.first_name,
          last_name: legislator.lastName ?? legislator.last_name,
          party_affiliation: legislator.partyAffiliation ?? legislator.party_affiliation,
          chamber: legislator.chamber,
          district: legislator.district,
          committees: legislator.committees || '',
          // No existing predictions since this is a new prediction session
          predicted_vote: undefined,
          confidence_level: undefined,
          prediction_notes: undefined,
          assignment_method: undefined,
          prediction_updated_at: undefined
        }));
        setLegislators(legislatorsData);
      } else {
        // Fallback: try to fetch all legislators without session_year filter
        const fallbackResponse = await api.get('/legislators');
        
        if (fallbackResponse.data.success && fallbackResponse.data.data && fallbackResponse.data.data.legislators) {
          const fallbackLegislatorsData = fallbackResponse.data.data.legislators.map((legislator: any) => ({
            id: legislator.id,
            // Same camelCase mapping as the primary path above.
            full_name: legislator.fullName ?? legislator.full_name,
            first_name: legislator.firstName ?? legislator.first_name,
            last_name: legislator.lastName ?? legislator.last_name,
            party_affiliation: legislator.partyAffiliation ?? legislator.party_affiliation,
            chamber: legislator.chamber,
            district: legislator.district,
            committees: legislator.committees || '',
            predicted_vote: undefined,
            confidence_level: undefined,
            prediction_notes: undefined,
            assignment_method: undefined,
            prediction_updated_at: undefined
          }));
          setLegislators(fallbackLegislatorsData);
        }
      }
    } catch (error) {
      console.error('Error fetching legislators:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/legislators/filter-options');
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Fetch available workspaces
  const fetchWorkspaces = async () => {
    try {
      const response = await api.get('/workspace-bill-tracking/workspaces');
      if (response.data.success) {
        setWorkspaces(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    }
  };

  // Handle vote assignment
  const handleVoteAssignment = async (
    legislatorId: number, 
    vote: string, 
    confidence?: string, 
    notes?: string
  ) => {
    if (!Array.isArray(legislators)) return;
    
    const legislator = legislators.find(l => l.id === legislatorId);
    const finalConfidence = confidence || legislator?.confidence_level || 'medium';
    const finalNotes = notes !== undefined ? notes : (legislator?.prediction_notes || '');
    
    // For now, we'll store predictions temporarily and save them when workspaces are selected
    setLegislators(prev => Array.isArray(prev) ? prev.map(l => 
      l.id === legislatorId 
        ? { ...l, predicted_vote: vote, confidence_level: finalConfidence, prediction_notes: finalNotes }
        : l
    ) : []);
  };

  // Handle filter change
  const handleFilterChange = (filterType: keyof typeof filters, value: string) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterType]: value
      };
      
      // If chamber changes, reset committee to "All committees"
      if (filterType === 'chamber') {
        newFilters.committee = 'All committees';
      }
      
      return newFilters;
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      chamber: 'All',
      committee: 'All committees',
      party: 'All parties'
    });
  };

  // Filter legislators (with safety check)
  const filteredLegislators = Array.isArray(legislators) ? legislators.filter(legislator => {
    const matchesChamber = filters.chamber === 'All' || legislator.chamber === filters.chamber;
    const matchesParty = filters.party === 'All parties' || matchesPartyFilter(legislator.party_affiliation, filters.party);
    const matchesCommittee = filters.committee === 'All committees' || 
      (legislator.committees && legislator.committees.toLowerCase().includes(filters.committee.toLowerCase()));

    return matchesChamber && matchesParty && matchesCommittee;
  }) : [];

  // Handle workspace selection
  const handleWorkspaceToggle = (workspaceId: string) => {
    setSelectedWorkspaces(prev => 
      prev.includes(workspaceId) 
        ? prev.filter(id => id !== workspaceId)
        : [...prev, workspaceId]
    );
  };

  // Save predictions to selected workspaces
  const handleSave = async () => {
    if (selectedWorkspaces.length === 0) {
      alert('Please select at least one workspace.');
      return;
    }

    if (!Array.isArray(legislators)) {
      alert('No legislators data available. Please try again.');
      return;
    }

    setSaving(true);
    try {
      // Save predictions for each workspace
      for (const workspaceId of selectedWorkspaces) {
        // First, ensure the bill is tracked in the workspace
        await api.post(`/workspace-bill-tracking/${workspaceId}`, {
          billId: parseInt(billId)
        });
        
        // Then save the predictions
        for (const legislator of legislators) {
          if (legislator.predicted_vote) {
            await api.post('/workspace-vote-predictions', {
              workspaceId,
              legislatorId: legislator.id,
              billId: parseInt(billId),
              predictedVote: legislator.predicted_vote,
              confidenceLevel: legislator.confidence_level || 'medium',
              predictionNotes: legislator.prediction_notes || ''
            });
          }
        }
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving predictions:', error);
      alert('Failed to save vote predictions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Notes handling functions
  const startEditingNotes = (legislatorId: number, currentNotes: string) => {
    setEditingNotes(prev => new Set([...prev, legislatorId]));
    setExpandedLegislators(prev => new Set([...prev, legislatorId])); // Ensure row is expanded
    setTempNotes(prev => ({ ...prev, [legislatorId]: currentNotes || '' }));
  };

  const cancelEditingNotes = (legislatorId: number) => {
    setEditingNotes(prev => {
      const newSet = new Set(prev);
      newSet.delete(legislatorId);
      return newSet;
    });
    setTempNotes(prev => {
      const newTemp = { ...prev };
      delete newTemp[legislatorId];
      return newTemp;
    });
  };

  const saveNotes = (legislatorId: number) => {
    const notes = tempNotes[legislatorId] || '';
    setLegislators(prev => Array.isArray(prev) ? prev.map(l => 
      l.id === legislatorId ? { ...l, prediction_notes: notes } : l
    ) : []);
    cancelEditingNotes(legislatorId);
  };

  const toggleLegislatorExpansion = (legislatorId: number) => {
    setExpandedLegislators(prev => {
      const newSet = new Set(prev);
      if (newSet.has(legislatorId)) {
        newSet.delete(legislatorId);
      } else {
        newSet.add(legislatorId);
      }
      return newSet;
    });
  };

  // Helper functions for Chip variants
  const getPartyVariant = (party: string): 'info' | 'danger' | 'neutral' => {
    if (party?.toLowerCase().includes('democrat')) {
      return 'info'; // Blue
    } else if (party?.toLowerCase().includes('republican')) {
      return 'danger'; // Red
    }
    return 'neutral';
  };

  const getVoteVariant = (vote: string): 'success' | 'danger' | 'warning' | 'neutral' => {
    switch (vote) {
      case 'yes': return 'success';
      case 'no': return 'danger';
      case 'abstain': return 'warning';
      case 'absent': return 'neutral';
      default: return 'neutral';
    }
  };

  const getVoteIcon = (vote: string) => {
    switch (vote) {
      case 'yes': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'no': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'abstain': return <Minus className="w-4 h-4 text-yellow-600" />;
      case 'absent': return <UserX className="w-4 h-4 text-neutral-600" />;
      default: return null;
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      title="Vote predictions"
      description={`${billNumber} - ${billTitle}`}
      onClose={onClose}
      size="xl"
      className="sm:max-w-6xl"
      /* The step indicator is the only thing saying which of the three screens
         you are on — it cannot scroll away. */
      subheader={
          <div className="flex items-center gap-2 text-sm">
            <span className={`${step === 'predictions' ? 'text-primary-600 font-medium' : 'text-neutral-500'}`}>
              1. Assign Vote Predictions
            </span>
            <ChevronRight className="w-4 h-4 text-neutral-500" />
            <span className={`${step === 'workspaces' ? 'text-primary-600 font-medium' : 'text-neutral-500'}`}>
              2. Select Workspaces
            </span>
          </div>
      }
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <div className="text-sm text-neutral-600 dark:text-text-dark-tertiary">
            {step === 'predictions' ? (
              `${filteredLegislators.filter(l => l.predicted_vote).length} of ${filteredLegislators.length} legislators have predictions`
            ) : (
              `${selectedWorkspaces.length} workspace(s) selected`
            )}
          </div>
          
          <div className="flex gap-3">
            {step === 'workspaces' && (
              <button
                onClick={() => setStep('predictions')}
                className="px-4 py-2 border border-border-light-secondary dark:border-border-dark-secondary text-neutral-700 dark:text-text-dark-secondary rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
              >
                Back
              </button>
            )}
            
            <button
              onClick={onClose}
              className="px-4 py-2 border border-border-light-secondary dark:border-border-dark-secondary text-neutral-700 dark:text-text-dark-secondary rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
            >
              Cancel
            </button>
            
            {step === 'predictions' ? (
              <button
                onClick={() => setStep('workspaces')}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Continue to Workspaces
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={selectedWorkspaces.length === 0 || saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-neutral-400 transition-colors flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Vote className="w-4 h-4" />
                    Save Predictions
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      }
    >
        {/* Content */}
        <div className="flex-1 overflow-y-auto max-h-[60vh]">
          {step === 'predictions' ? (
            <div className="p-6 space-y-6">
              {/* Filters */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                    Filter Legislators
                  </h3>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-neutral-600 dark:text-text-dark-tertiary hover:text-neutral-800 dark:hover:text-text-dark-primary"
                  >
                    Clear filters
                  </button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Chamber Filter */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                      Chamber
                    </label>
                    <select
                      value={filters.chamber}
                      onChange={(e) => handleFilterChange('chamber', e.target.value)}
                      className="input text-sm"
                    >
                      <option value="All">All Chambers</option>
                      {filterOptions.chambers.map((chamber) => (
                        <option key={chamber} value={chamber}>{chamber}</option>
                      ))}
                    </select>
                  </div>

                  {/* Committee Filter */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                      Committee
                    </label>
                    <select
                      value={filters.committee}
                      onChange={(e) => handleFilterChange('committee', e.target.value)}
                      className="input text-sm"
                    >
                      <option value="All committees">All Committees</option>
                      {filterOptions.committees
                        .filter(committee => 
                          filters.chamber === 'All' || committee.chamber === filters.chamber
                        )
                        .map((committee) => (
                          <option key={`${committee.name}-${committee.chamber}`} value={committee.name}>
                            {committee.displayName}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Party Filter */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                      Party
                    </label>
                    <select
                      value={filters.party}
                      onChange={(e) => handleFilterChange('party', e.target.value)}
                      className="input text-sm"
                    >
                      <option value="All parties">All Parties</option>
                      {filterOptions.parties.map((party) => (
                        <option key={party} value={party}>{party}</option>
                      ))}
                    </select>
                  </div>

                  {/* Confidence filter removed - not applicable for new predictions */}
                </div>
              </div>

              {/* Legislators Table */}
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
                  Legislators ({filteredLegislators.length})
                </h3>
                
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-neutral-500" />
                  </div>
                ) : filteredLegislators.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-neutral-600 dark:text-text-dark-tertiary">
                      No legislators found matching your filters.
                    </p>
                  </div>
                ) : (
                  <StandardTable horizontalScroll>
                    {/*
                      StandardTable, not a hand-rolled <table>. The head was
                      `bg-background-secondary` with grey uppercase labels; the app
                      standard is `thead.table-header` — primary-800 fill, white bold
                      uppercase — which StandardTableHeader applies.
                    */}
                    <StandardTableHeader>
                      <tr>
                        <StandardTableHeaderCell>Legislator</StandardTableHeaderCell>
                        <StandardTableHeaderCell>Party</StandardTableHeaderCell>
                        <StandardTableHeaderCell>Chamber</StandardTableHeaderCell>
                        <StandardTableHeaderCell>Vote</StandardTableHeaderCell>
                        <StandardTableHeaderCell>Confidence</StandardTableHeaderCell>
                        <StandardTableHeaderCell>Assign</StandardTableHeaderCell>
                        <StandardTableHeaderCell>Notes</StandardTableHeaderCell>
                      </tr>
                    </StandardTableHeader>
                    <StandardTableBody>
                        {filteredLegislators.map((legislator) => (
                          <React.Fragment key={legislator.id}>
                            <StandardTableRow>
                              {/* Legislator Name */}
                              <StandardTableCell>
                                <div className="font-medium text-neutral-900 dark:text-white">
                                  {formatLegislatorNameFromObject(legislator)}
                                </div>
                              </StandardTableCell>
                              
                              {/* Party */}
                              <StandardTableCell>
                                <Chip 
                                  label={formatPartyDisplay(legislator.party_affiliation)} 
                                  variant={getPartyVariant(legislator.party_affiliation)} 
                                  size="sm" 
                                />
                              </StandardTableCell>
                              
                              {/* Chamber */}
                              <StandardTableCell>
                                {legislator.chamber}
                              </StandardTableCell>
                              
                              {/* Current Vote */}
                              <StandardTableCell>
                                {legislator.predicted_vote ? (
                                  <div className="flex items-center gap-1">
                                    {getVoteIcon(legislator.predicted_vote)}
                                    <Chip 
                                      label={legislator.predicted_vote} 
                                      variant={getVoteVariant(legislator.predicted_vote)} 
                                      size="sm" 
                                    />
                                  </div>
                                ) : (
                                  <span className="text-sm text-neutral-500 dark:text-text-dark-tertiary">Not set</span>
                                )}
                              </StandardTableCell>
                              
                              {/* Confidence Level */}
                              <StandardTableCell>
                                <select
                                  value={legislator.confidence_level || 'medium'}
                                  onChange={(e) => handleVoteAssignment(legislator.id, legislator.predicted_vote || 'abstain', e.target.value)}
                                  className="text-sm border border-border-light-secondary dark:border-border-dark-secondary rounded-md px-2 py-1 bg-white dark:bg-background-dark-tertiary text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                  <option value="low">Low</option>
                                  <option value="medium">Medium</option>
                                  <option value="high">High</option>
                                </select>
                              </StandardTableCell>
                              
                              {/* Vote Assignment Buttons */}
                              <StandardTableCell>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handleVoteAssignment(legislator.id, 'yes')}
                                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                      legislator.predicted_vote === 'yes'
                                        ? 'bg-green-600 text-white'
                                        : 'bg-background-tertiary text-neutral-700 hover:bg-green-100 hover:text-green-700 dark:bg-neutral-600 dark:text-text-dark-secondary dark:hover:bg-green-600 dark:hover:text-white'
                                    }`}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() => handleVoteAssignment(legislator.id, 'no')}
                                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                      legislator.predicted_vote === 'no'
                                        ? 'bg-red-600 text-white'
                                        : 'bg-background-tertiary text-neutral-700 hover:bg-red-100 hover:text-red-700 dark:bg-neutral-600 dark:text-text-dark-secondary dark:hover:bg-red-600 dark:hover:text-white'
                                    }`}
                                  >
                                    No
                                  </button>
                                  <button
                                    onClick={() => handleVoteAssignment(legislator.id, 'abstain')}
                                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                      legislator.predicted_vote === 'abstain'
                                        ? 'bg-yellow-600 text-white'
                                        : 'bg-background-tertiary text-neutral-700 hover:bg-yellow-100 hover:text-yellow-700 dark:bg-neutral-600 dark:text-text-dark-secondary dark:hover:bg-yellow-600 dark:hover:text-white'
                                    }`}
                                  >
                                    Abstain
                                  </button>
                                  <button
                                    onClick={() => handleVoteAssignment(legislator.id, 'absent')}
                                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                      legislator.predicted_vote === 'absent'
                                        ? 'bg-neutral-600 text-white'
                                        : 'bg-background-tertiary text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-600 dark:text-text-dark-secondary dark:hover:bg-neutral-500'
                                    }`}
                                  >
                                    Absent
                                  </button>
                                </div>
                              </StandardTableCell>
                              
                              {/* Notes */}
                              <StandardTableCell>
                                <div className="flex items-center gap-2">
                                  {legislator.prediction_notes ? (
                                    <button
                                      onClick={() => toggleLegislatorExpansion(legislator.id)}
                                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                      title={legislator.prediction_notes}
                                    >
                                      <StickyNote className="w-4 h-4" />
                                      {expandedLegislators.has(legislator.id) ? 'Hide' : 'View'}
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => startEditingNotes(legislator.id, '')}
                                      className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 dark:text-text-dark-tertiary dark:hover:text-text-dark-secondary"
                                    >
                                      <StickyNote className="w-4 h-4" />
                                      Add
                                    </button>
                                  )}
                                </div>
                              </StandardTableCell>
                            </StandardTableRow>

                            {/* Expanded Notes Row */}
                            {expandedLegislators.has(legislator.id) && (
                              <StandardTableRow>
                                <StandardTableCell colSpan={7} className="surface-inset">
                                  <div className="space-y-3">
                                    {/* Existing Notes Display */}
                                    {legislator.prediction_notes && !editingNotes.has(legislator.id) && (
                                      <div className="card p-3">
                                        <div className="flex items-start justify-between">
                                          <p className="text-sm text-neutral-700 dark:text-text-dark-secondary whitespace-pre-wrap flex-1">
                                            {legislator.prediction_notes}
                                          </p>
                                          <button
                                            onClick={() => startEditingNotes(legislator.id, legislator.prediction_notes || '')}
                                            className="ml-2 p-1 text-neutral-500 hover:text-neutral-600 dark:hover:text-text-dark-secondary transition-colors"
                                            title="Edit notes"
                                          >
                                            <EditIcon className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {/* Notes Editor */}
                                    {(editingNotes.has(legislator.id) || (!legislator.prediction_notes && expandedLegislators.has(legislator.id))) && (
                                      <div className="card p-3">
                                        <textarea
                                          value={tempNotes[legislator.id] || ''}
                                          onChange={(e) => setTempNotes(prev => ({ ...prev, [legislator.id]: e.target.value }))}
                                          placeholder={`Add notes about ${legislator.full_name}...`}
                                          className="input h-20 text-sm resize-none"
                                        />
                                        <div className="flex justify-end gap-2 mt-2">
                                          <button
                                            onClick={() => cancelEditingNotes(legislator.id)}
                                            className="px-2 py-1 text-xs text-neutral-600 hover:text-neutral-800 dark:text-text-dark-tertiary dark:hover:text-text-dark-primary transition-colors"
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            onClick={() => saveNotes(legislator.id)}
                                            className="px-3 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 transition-colors flex items-center gap-1"
                                          >
                                            <Save className="w-3 h-3" />
                                            Save
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </StandardTableCell>
                              </StandardTableRow>
                            )}
                          </React.Fragment>
                        ))}
                    </StandardTableBody>
                  </StandardTable>
                )}
              </div>
            </div>
          ) : (
            /* Workspace Selection Step */
            <div className="p-6">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
                Select Workspaces to Save Predictions
              </h3>
              
              {workspaces.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-neutral-600 dark:text-text-dark-tertiary">
                    No workspaces available.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {workspaces.map((workspace) => (
                    <label key={workspace.id} className="flex items-center space-x-3 p-3 border border-border-light dark:border-border-dark-hairline rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedWorkspaces.includes(workspace.id)}
                        onChange={() => handleWorkspaceToggle(workspace.id)}
                        className="w-4 h-4 text-primary-600 border-border-light-secondary rounded focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-neutral-900 dark:text-white">
                          {workspace.name}
                        </div>
                        <div className="text-sm text-neutral-500 dark:text-text-dark-tertiary">
                          Access: {workspace.access_level}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

    </Modal>
  );
};

export default VotePredictionModal;
